(() => {
  'use strict';

  class AgeVerifier {
    constructor(element) {
      this.element = element;
      this.dialog = element.querySelector('[data-age-verifier-dialog]');
      this.yesButton = element.querySelector('[data-age-verifier-yes]');
      this.noButton = element.querySelector('[data-age-verifier-no]');
      this.error = element.querySelector('[data-age-verifier-error]');

      this.storageKey =
        element.dataset.ageVerifierKey || 'age_verifier_verified';

      this.delay =
        Number(element.dataset.ageVerifierDelay) || 0;

      this.validity =
        element.dataset.ageVerifierValidity || 'forever';

      this.previousActiveElement = null;
      this.delayTimer = null;
      this.scrollY = 0;

      this.handleYes = this.handleYes.bind(this);
      this.handleNo = this.handleNo.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.init();
    }

    init() {
      if (!this.element) {
        return;
      }

      this.yesButton?.addEventListener('click', this.handleYes);
      this.noButton?.addEventListener('click', this.handleNo);

      if (this.isVerified()) {
        return;
      }

      if (this.delay > 0) {
        this.delayTimer = window.setTimeout(() => {
          this.open();
        }, this.delay * 1000);
      } else {
        this.open();
      }
    }

    isVerified() {
    try {
        if (this.validity === 'session') {
        return sessionStorage.getItem(this.storageKey) === 'true';
        }

        const storedVerification =
        localStorage.getItem(this.storageKey);

        if (!storedVerification) {
        return false;
        }

        if (this.validity === 'forever') {
        return true;
        }

        const verificationData =
        JSON.parse(storedVerification);

        if (
        !verificationData ||
        !verificationData.timestamp
        ) {
        localStorage.removeItem(this.storageKey);
        return false;
        }

        const validityDays = Number(this.validity);

        if (!validityDays || validityDays <= 0) {
        localStorage.removeItem(this.storageKey);
        return false;
        }

        const expirationTime =
        verificationData.timestamp +
        validityDays * 24 * 60 * 60 * 1000;

        if (Date.now() < expirationTime) {
        return true;
        }

        localStorage.removeItem(this.storageKey);

        return false;
    } catch (error) {
        return false;
    }
    }

    saveVerification() {
      try {
        if (this.validity === 'session') {
          sessionStorage.setItem(
            this.storageKey,
            'true'
          );

          return;
        }

        localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            timestamp: Date.now()
          })
        );
      } catch (error) {
        // Storage may be unavailable.
      }
    }

    open() {
      this.previousActiveElement = document.activeElement;

      this.scrollY = window.scrollY;

      this.element.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add(
        'age-verifier-active'
      );

      document.body.classList.add(
        'age-verifier-active'
      );

      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      document.documentElement.style.overflow = 'hidden';

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      requestAnimationFrame(() => {
        this.dialog?.focus();
      });
    }

    close() {
      this.element.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove(
        'age-verifier-active'
      );

      document.body.classList.remove(
        'age-verifier-active'
      );

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';

      document.documentElement.style.overflow = '';

      window.scrollTo(
        0,
        this.scrollY || 0
      );

      if (
        this.previousActiveElement &&
        typeof this.previousActiveElement.focus === 'function'
      ) {
        this.previousActiveElement.focus();
      }
    }

    handleYes() {
      this.hideError();
      this.saveVerification();
      this.close();
    }

    handleNo() {
      this.showError();
    }

    showError() {
    if (!this.error) {
        return;
    }

    this.error.hidden = false;
    this.error.setAttribute('aria-hidden', 'false');

    this.noButton?.setAttribute(
        'aria-describedby',
        this.error.id || ''
    );

    // Restart the animation every time NO is clicked.
    this.error.classList.remove('age-verifier__error--shake');

    // Force browser reflow so the animation can restart.
    void this.error.offsetWidth;

    this.error.classList.add('age-verifier__error--shake');

    this.dialog?.focus();
    }

    hideError() {
      if (!this.error) {
        return;
      }

      this.error.hidden = true;

      this.error.setAttribute(
        'aria-hidden',
        'true'
      );

      this.noButton?.removeAttribute(
        'aria-describedby'
      );
    }

    handleKeydown(event) {
      if (
        this.element.getAttribute(
          'aria-hidden'
        ) !== 'false'
      ) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      this.trapFocus(event);
    }

    trapFocus(event) {
      const focusableElements =
        this.dialog?.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

      if (!focusableElements?.length) {
        event.preventDefault();

        return;
      }

      const firstElement =
        focusableElements[0];

      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();

        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();

        firstElement.focus();
      }
    }
  }

  const initAgeVerifier = () => {
    const verifier =
      document.querySelector(
        '[data-age-verifier]'
      );

    if (
      !verifier ||
      verifier.dataset.ageVerifierInitialized === 'true'
    ) {
      return;
    }

    verifier.dataset.ageVerifierInitialized = 'true';

    new AgeVerifier(verifier);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAgeVerifier
    );
  } else {
    initAgeVerifier();
  }
})();